import type { EChartsType } from 'echarts/core';
import type { CallbackDataParams, EChartsOption } from 'echarts/types/dist/shared';
import React, { useEffect, useRef } from 'react';
import {
    getChartOptionsWithRuntimeFonts,
    getEchartsOptions,
    loadEcharts,
} from '@meitu/saas-editor-core/es/layers/chart-layer/utils/core';
import { checkChartIsEmpty, EMPTY_CHART_MAP } from '@meitu/saas-editor-core/es/layers/chart-layer/utils/render';
import type { IChartLayer } from '@meitu/saas-editor-core/es/version-types';
import { useLocaleStore } from '@/i18n';

const CHART_PREVIEW_ANIMATION_DURATION = 800;
type ChartActionTarget = { dataIndex: number; seriesIndex: number };

const getInitSeriesAnimationOptions = (options: EChartsOption): EChartsOption => {
    const series = Array.isArray(options.series) ? options.series : options.series ? [options.series] : [];

    return {
        ...options,
        animation: false,
        series: series.map((item) => {
            if (item.type === 'line') {
                return {
                    ...item,
                    animation: false,
                };
            }

            return {
                ...item,
                animation: true,
                animationDuration: CHART_PREVIEW_ANIMATION_DURATION,
                animationDurationUpdate: 0,
                animationEasing: 'cubicOut',
            };
        }),
    };
};

const getChartActionTarget = (params: unknown): ChartActionTarget | null => {
    const maybeParams = params as Partial<CallbackDataParams>;
    if (typeof maybeParams.seriesIndex !== 'number' || typeof maybeParams.dataIndex !== 'number') return null;

    return {
        seriesIndex: maybeParams.seriesIndex,
        dataIndex: maybeParams.dataIndex,
    };
};

const getChartActionTargetKey = (target: ChartActionTarget) => `${target.seriesIndex}:${target.dataIndex}`;

const findChartActionTargetIndex = (targets: ChartActionTarget[], target: ChartActionTarget) => {
    const targetKey = getChartActionTargetKey(target);
    return targets.findIndex((item) => getChartActionTargetKey(item) === targetKey);
};

const getChartSelectionGroupTargets = (
    layer: Pick<IChartLayer, 'data'>,
    target: ChartActionTarget,
): ChartActionTarget[] => {
    const isPieChart = layer.data.type === 'pie-basic' || layer.data.type === 'pie-doughnut';
    if (isPieChart) return [target];

    return layer.data.chartData.slice(1).map((_, dataIndex) => ({
        seriesIndex: target.seriesIndex,
        dataIndex,
    }));
};

const checkIsChartSelectionExclusive = (layer: Pick<IChartLayer, 'data'>) => {
    return layer.data.type === 'pie-basic' || layer.data.type === 'pie-doughnut';
};

const ChartPreview: React.FC<{
    layer: Pick<IChartLayer, 'width' | 'height' | 'data' | '__runtime'>;
}> = ({ layer }) => {
    const { $t } = useLocaleStore();
    const containerRef = useRef<HTMLDivElement>(null);
    const chartInstanceRef = useRef<EChartsType | null>(null);
    const isInitRenderRef = useRef(true);
    const selectedTargetsRef = useRef<ChartActionTarget[]>([]);

    useEffect(() => {
        if (!containerRef.current || checkChartIsEmpty(layer)) {
            chartInstanceRef.current?.dispose();
            chartInstanceRef.current = null;
            return;
        }

        let disposed = false;
        let handleChartClick: ((params: unknown) => void) | null = null;
        const layerData = getChartOptionsWithRuntimeFonts(layer.data, layer.__runtime?.fontFamilyMap ?? {});
        selectedTargetsRef.current = [];

        loadEcharts().then((echarts) => {
            if (disposed || !containerRef.current) return;

            const chartInstance =
                chartInstanceRef.current ??
                echarts.init(containerRef.current, null, {
                    renderer: 'canvas',
                    width: layer.width,
                    height: layer.height,
                });

            chartInstanceRef.current = chartInstance;
            handleChartClick = (params: unknown) => {
                const target = getChartActionTarget(params);
                if (!target) return;

                const groupTargets = getChartSelectionGroupTargets(layer, target);
                if (!groupTargets.length) return;

                const isGroupSelected = groupTargets.every(
                    (item) => findChartActionTargetIndex(selectedTargetsRef.current, item) !== -1,
                );

                if (checkIsChartSelectionExclusive(layer)) {
                    if (isGroupSelected && selectedTargetsRef.current.length === groupTargets.length) {
                        groupTargets.forEach((item) => {
                            chartInstance.dispatchAction({
                                type: 'unselect',
                                ...item,
                            });
                        });
                        selectedTargetsRef.current = [];
                        return;
                    }

                    selectedTargetsRef.current.forEach((item) => {
                        chartInstance.dispatchAction({
                            type: 'unselect',
                            ...item,
                        });
                    });
                    groupTargets.forEach((item) => {
                        chartInstance.dispatchAction({
                            type: 'select',
                            ...item,
                        });
                    });
                    selectedTargetsRef.current = groupTargets;
                    return;
                }

                const shouldSelect = !isGroupSelected;

                groupTargets.forEach((item) => {
                    const selectedIndex = findChartActionTargetIndex(selectedTargetsRef.current, item);
                    const isSelected = selectedIndex !== -1;
                    if (isSelected === shouldSelect) return;

                    chartInstance.dispatchAction({
                        type: shouldSelect ? 'select' : 'unselect',
                        ...item,
                    });
                    if (shouldSelect) {
                        selectedTargetsRef.current.push(item);
                    } else {
                        selectedTargetsRef.current.splice(selectedIndex, 1);
                    }
                });
            };
            chartInstance.on('click', handleChartClick);
            const isInitRender = isInitRenderRef.current;
            isInitRenderRef.current = false;
            chartInstance.resize({
                width: layer.width,
                height: layer.height,
            });

            const finalOptions = getEchartsOptions(layerData, {
                type: layer.data.type,
                chartWidth: layer.width,
                chartHeight: layer.height,
            });

            if (isInitRender) {
                chartInstance.setOption(getInitSeriesAnimationOptions(finalOptions), {
                    notMerge: true,
                    silent: true,
                    lazyUpdate: false,
                });
                return;
            }

            chartInstance.setOption(
                {
                    ...finalOptions,
                    animation: false,
                },
                {
                    notMerge: true,
                    silent: true,
                    lazyUpdate: false,
                },
            );
        });

        return () => {
            disposed = true;
            if (handleChartClick) {
                chartInstanceRef.current?.off('click', handleChartClick);
            }
        };
    }, [layer]);

    useEffect(() => {
        return () => {
            chartInstanceRef.current?.dispose();
            chartInstanceRef.current = null;
        };
    }, []);

    if (checkChartIsEmpty(layer)) {
        return (
            <div
                className="chartPreviewEmpty"
                style={{ '--w': `${Math.min(layer.width, layer.height)}px` } as React.CSSProperties}
            >
                <img src={EMPTY_CHART_MAP[layer.data.type]} alt="" />
                <span>{$t((v) => v.chartLayer.emptyChart)}</span>
            </div>
        );
    }

    return <div ref={containerRef} style={{ width: layer.width, height: layer.height }} />;
};

ChartPreview.displayName = 'ChartPreview';

export default ChartPreview;
